import { State, Action, StateContext, Selector, Store, Actions, ofActionDispatched } from '@ngxs/store';
import { EngineStateModel } from './engine.state.model';
import {
  Load,
  LoadSuccess,
  SetReady,
  SetGraph,
  SetEngineChapter,
  SetEnginePosition,
  SetEngineChapterInit,
  SynchronizeVisibleNodes,
  ClearGraph,
  ResetEnginePosition,
  ToggleCameraBar,
  ToggleLabels,
  SetActiveTool,
  PlayAnimation,
  CenterCameraOnVisible,
  SetDisplay,
  SetVisible,
  SynchronizeRenderableNodes,
  SynchronizePickableNodes,
  SetScrollMode,
  SetXrayMode,
  SetXrayModeCacheColorRecipes,
} from './engine.actions';
import { BdGraphService } from '../../../lib/graph/graph.service';
import { ViewTreeNode } from '../models/view.tree-node';
import { EngineService } from '../engine.service';
import { BdGraph, BdGraphStateMap, BdGraphFactory } from 'src/lib/graph/graph.model';
import { EnginePositionModel } from '../models/engine-position.model';
import { EngineVisibilityEnum } from '../models/engine-visibility.enum';
import { EngineCameraState } from './camera/engine.camera.state';
import { ToolEnum } from '../models/tool.enum';
import { content } from 'html2canvas/dist/types/css/property-descriptors/content';
import { ScrollModeEnum } from '../models/scroll-mode.enum';

import { SetObjectLayerOrder } from './engine.actions';

declare const Human: any;

const enginePositionDefaults = new EnginePositionModel(0, 0, 0, 0, EngineVisibilityEnum.Hidden);


@State<EngineStateModel>({
  name: 'engine',
  defaults: {
    loading: false,
    ready: false,
    viewGraph: BdGraphFactory<ViewTreeNode>(),
    engine: '',
    chapter: '',
    chapterInit: {
      id: '',
      init: false,
      camera: false,
      annotations: false,
      graph: false,
      properties: false,
      particles: false,
      clips: false,
      postprocessing: false,
      transparency: false
    },
    showCameraBar: true,
    showLabels: true,
    activeTool: ToolEnum.Select,
    animationPlaying: true,
    display: true,
    visible: true,
    scrollMode: ScrollModeEnum.Capture,
    xrayModeEnabled: false,
    xrayModeCachedColorRecipes: []
  },
  children: [
    EngineCameraState
  ]
})
export class EngineState {
  /**
   * Selectors
   */
  @Selector()
  static getEngine(state: EngineStateModel) {
    return state.engine;
  }

  @Selector()
  static getReady(state: EngineStateModel) {
    return state.ready;
  }

  @Selector()
  static getDisplay(state: EngineStateModel) {
    return state.display;
  }

  @Selector()
  static getScrollMode(state: EngineStateModel) {
    return state.scrollMode;
  }

  @Selector()
  static getVisible(state: EngineStateModel) {
    return state.visible;
  }

  @Selector()
  static getViewGraph(state: EngineStateModel) {
    return state.viewGraph;
  }

  @Selector()
  static getViewGraphNode(state: EngineStateModel) {
    return (objectId: string) => {
      return state.viewGraph.nodes[objectId];
    };
  }


  @Selector()
  static getEnginePosition(state: EngineStateModel) {
    return state.enginePosition;
  }

  @Selector()
  static getChapter(state: EngineStateModel) {
    return state.chapter;
  }

  @Selector()
  static getActiveTool(state: EngineStateModel) {
    return state.activeTool;
  }

  @Selector()
  static getShowCameraBar(state: EngineStateModel) {
    return state.showCameraBar;
  }

  @Selector()
  static getShowLabels(state: EngineStateModel) {
    return state.showLabels;
  }

  @Selector()
  static getChapterInit(state: EngineStateModel) {
    return state.chapterInit;
  }

  @Selector()
  static getAnimationPlaying(state: EngineStateModel) {
    return state.animationPlaying;
  }

  @Selector()
  static getXrayModeEnabled(state: EngineStateModel) {
    return state.xrayModeEnabled;
  }

  @Selector()
  static getXrayCachedColorRecipes(state: EngineStateModel) {
    return state.xrayModeCachedColorRecipes;
  }


  /**
   * Actions
   */
  @Action(Load)
  load(context: StateContext<EngineStateModel>, action: Load) {
    context.dispatch(new LoadSuccess('engine'));
  }

  @Action(LoadSuccess)
  loadSuccess({ patchState }: StateContext<EngineStateModel>, action: LoadSuccess) {
    patchState({
      engine: action.result
    });
  }

  @Action(SetReady)
  setReady(context: StateContext<EngineStateModel>, action: SetReady) {
    context.patchState({
      ready: action.ready
    });
  }

  @Action(SetDisplay)
  setDisplay(context: StateContext<EngineStateModel>, action: SetDisplay) {
    context.patchState({
      display: action.display
    });
  }

  @Action(SetScrollMode)
  setZoomViaScrollAllowed(context: StateContext<EngineStateModel>, action: SetScrollMode) {
    context.patchState({
      scrollMode: action.scrollMode
    });
  }

  @Action(SetVisible)
  setVisible(context: StateContext<EngineStateModel>, action: SetVisible) {
    context.patchState({
      visible: action.visible
    });
  }

  @Action(SetEnginePosition)
  setEnginePosition(context: StateContext<EngineStateModel>, action: SetEnginePosition) {
    context.patchState({
      enginePosition: {
        ...action.position,
        right: action.position.left + action.position.width,
        bottom: action.position.top + action.position.height
      }
    });
  }

  @Action(ResetEnginePosition)
  resetEnginePosition(context: StateContext<EngineStateModel>, action: ResetEnginePosition) {
    context.patchState({
      enginePosition: enginePositionDefaults
    });
  }

  @Action(SetEngineChapter)
  setChapter(context: StateContext<EngineStateModel>, action: SetEngineChapter) {
    context.patchState({
      chapter: action.chapterId
    });
  }

  @Action(SetEngineChapterInit)
  setChapterInit(context: StateContext<EngineStateModel>, action: SetEngineChapterInit) {
    context.patchState({
      chapterInit: action.chapterInit
    });
  }

  @Action(SetActiveTool)
  setActiveTool(context: StateContext<EngineStateModel>, action: SetActiveTool) {
    context.patchState({
      activeTool: action.tool
    });
  }

  /**
   * Graph Actions #TODO: Move into own state
   */
  @Action(SetGraph)
  setGraph(context: StateContext<EngineStateModel>, action: SetGraph) {
    // Set graph state
    context.patchState({
      viewGraph: {...action.graph}
    });
  }

  @Action(ClearGraph)
  clearGraph(context: StateContext<EngineStateModel>, action: SetGraph) {
    context.patchState({
      viewGraph: BdGraphFactory<ViewTreeNode>()
    });
  }

  @Action(SetXrayMode)
  setXrayMode(context: StateContext<EngineStateModel>, action: SetXrayMode) {
    context.patchState({
      xrayModeEnabled: action.enabled
    });
  }

  @Action(SetXrayModeCacheColorRecipes)
  setXrayModeCacheColorRecipes(context: StateContext<EngineStateModel>, action: SetXrayModeCacheColorRecipes) {
    context.patchState({
      xrayModeCachedColorRecipes: [...action.cachedColorRecipes]
    });
  }

  @Action(CenterCameraOnVisible)
  centerCameraOnVisible(context: StateContext<EngineStateModel>, action: CenterCameraOnVisible) {
    const visibleLeaves = this.engineService.getVisibleLeaves();
    const boundary = this.engineService.getBoundaryFromObjects(visibleLeaves);
    const lookAt = this.engineService.getCameraLookAt();
    lookAt.look = {
      x: (boundary.xmin + boundary.xmax) / 2.0,
      y: (boundary.ymin + boundary.ymax) / 2.0,
      z: (boundary.zmin + boundary.zmax) / 2.0
    };
    Human.renderer.camera.fly.flyTo(lookAt);
  }

  @Action(ToggleCameraBar)
  toggleCameraBar(context: StateContext<EngineStateModel>, action: ToggleCameraBar) {
    context.patchState({
      showCameraBar: action.show
    });
  }

  @Action(ToggleLabels)
  toggleLabels(context: StateContext<EngineStateModel>, action: ToggleLabels) {
    const show = (action.show != null) ? action.show : !context.getState().showLabels;

    context.patchState({
      showLabels: show
    });
  }

  @Action(SynchronizeVisibleNodes)
  synchronizeVisibleNodes(context: StateContext<EngineStateModel>, action: SynchronizeVisibleNodes) {
    const viewGraph = context.getState().viewGraph;
    const leafViewNodeIds = this.stateMapToNodeIds(viewGraph, action.leafVisibilityState);

    // set unique view node leaf state by querying corresponding human object state
    const leafState = {};
    leafViewNodeIds.forEach(viewNodeId => {
      const leafids = this.engineService.getLeafObjects(viewNodeId);
      const visibility = leafids.reduce((vis, objectId) => vis || Human.scene.objects[objectId].shown, false);

      leafState[viewNodeId] = { 'shown': visibility };
    });
    this.graphService.synchronize(viewGraph, { 'shown': 'trueWins' }, leafState);

    // patch the state
    context.patchState({
      viewGraph: { ...viewGraph }
    });
  }

  @Action(SynchronizeRenderableNodes)
  synchronizeRenderableNodes(context: StateContext<EngineStateModel>, action: SynchronizeRenderableNodes) {
    const viewGraph = context.getState().viewGraph;
    const leafViewNodeIds = this.stateMapToNodeIds(viewGraph, action.leafRenderableState);

    // view graph leaf state
    const leafState = {};
    leafViewNodeIds.forEach(leafId => {
      leafState[leafId] = { 'renderable': true };
    });
    this.graphService.synchronize(viewGraph, { 'renderable': 'falseWins' }, leafState);
  }

  @Action(SynchronizePickableNodes)
  synchronizePickableNodes(context: StateContext<EngineStateModel>, action: SynchronizePickableNodes) {
    const viewGraph = context.getState().viewGraph;
    const leafViewNodeIds = this.stateMapToNodeIds(viewGraph, action.leafPickableState);

    // set unique view node leaf state by querying corresponding human object state
    const leafState = {};
    leafViewNodeIds.forEach(viewNodeId => {
      const leafids = this.engineService.getLeafObjects(viewNodeId);
      const pickable = leafids.reduce((pick, objectId) => pick || Human.scene.objects[objectId].pickable, false);

      leafState[viewNodeId] = { 'pickable': pickable };
    });
    this.graphService.synchronize(viewGraph, { 'pickable': 'trueWins' }, leafState);

    // patch the state
    context.patchState({
      viewGraph: { ...viewGraph }
    });
  }

  @Action(PlayAnimation)
  playAnimation(context: StateContext<EngineStateModel>, action: PlayAnimation) {
    context.patchState({
      animationPlaying: action.play
    });
  }

  @Action(SetObjectLayerOrder)
  setObjectLayerOrder(context: StateContext<EngineStateModel>, action: SetObjectLayerOrder) {

    const viewGraph = context.getState().viewGraph;
    const layer = isNaN(action.layer) ? 0 : action.layer;

    let nodeIds = [];

    action.nodes.forEach((node) => {
      const descendantIds = this.graphService.getDescendants(viewGraph, node.id, true);
      nodeIds = nodeIds.concat(descendantIds);
    });

    nodeIds.forEach((nodeId) => {
      this.engineService.setObjectLayer(nodeId, layer);
      this.graphService.setState(viewGraph, nodeId, { layer: layer });
    });

    // patch the state
    context.patchState({
      viewGraph: { ...viewGraph }
    });
  }

  private stateMapToNodeIds(viewGraph: BdGraph<ViewTreeNode>, stateMap: BdGraphStateMap): Set<string> {
    // map human objects to relevant view nodes (if any);
    const ids = Object.keys(stateMap)
      .map(objectId => this.engineService.getNode(viewGraph, objectId))
      .filter(node => !!node)
      .map(node => node.id);

    return new Set<string>(ids);
  }

  constructor(
    private graphService: BdGraphService,
    private engineService: EngineService
  ) { }
}
